// src/AdminPanel/BulkAddProduct.js
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import { collection, addDoc } from "firebase/firestore";
import toast from "../utils/toast";
import { useSale } from "../contexts/SaleContext";
import CloudinaryUpload from "../components/CloudinaryUpload";
import {
  TYPE_OPTIONS,
  WEIGHT_TYPES,
  WEIGHT_QUANTITY_SUGGESTIONS,
  getProductBehaviorType,
  PRODUCT_BEHAVIOR,
} from "../utils/productUtils";

const nameOptions = [
  "Panadol Tablets 500mg", "Vitamin C 1000mg", "Glutamax Capsules",
  "Arinac Forte 400/60mg", "Ibuprofen 200mg"
];
const manufacturedByOptions = ["Pfizer", "GSK", "Abbott", "Sanofi", "Novartis"];
const descriptionOptions = [
  "Pain relief tablets", "Vitamin supplement",
  "Joint support capsules", "Anti-inflammatory medication"
];
const usageOptions = [
  "Take 1 tablet daily", "Take 2 capsules after meals",
  "Apply topically twice a day", "Take with water"
];

const emptyProduct = () => ({
  name: "",
  formula: "",
  type: "",
  manufacturedBy: "",
  picture: "",
  description: "",
  usage: "",
  price: "",
  retailPrice: "",
  quantityPerPack: "",
  stripsPerPack: "",
  tabletsPerStrip: "",
  packEnabled: true,
  packPrice: "",
  packStock: "",
  stripEnabled: false,
  stripPrice: "",
  stripStock: "",
});

function BulkAddProduct() {
  const navigate = useNavigate();
  const { discountLabel, categories } = useSale();
  const [products, setProducts] = useState([emptyProduct()]);
  const [loading, setLoading] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const isSubmitting = useRef(false); // prevents double-fire from two Submit buttons

  const updateField = (index, field, value) => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addNewRow = () => {
    setProducts((prev) => [...prev, emptyProduct()]);
  };

  const removeRow = (index) => {
    if (products.length === 1) return; // keep at least one
    setProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const isRowValid = (p) => {
    const isMedicine = getProductBehaviorType(p.type) === PRODUCT_BEHAVIOR.MEDICINE;
    const medicineValid =
      (p.packEnabled ? p.packPrice !== "" : true) &&
      (p.stripEnabled ? p.stripPrice !== "" : true) &&
      (p.packEnabled || p.stripEnabled);
    return (
      p.name.trim() !== "" &&
      p.type.trim() !== "" &&
      p.picture.trim() !== "" &&
      (isMedicine ? medicineValid : p.price !== "")
    );
  };

  const allValid = products.every(isRowValid);

  const handleSubmitAll = async () => {
    if (isSubmitting.current) return; // block any double-click / second button race
    if (!allValid) {
      toast.error("Each product requires Name, Category, Price, and Image.");
      return;
    }
    isSubmitting.current = true;
    setLoading(true);
    setSubmittedCount(0);
    let successCount = 0;
    let failCount = 0;

    // Add all products concurrently for real-time sync
    const promises = products.map(async (p) => {
      const behavior = getProductBehaviorType(p.type);
      const isMedicine = behavior === PRODUCT_BEHAVIOR.MEDICINE;
      const isWeight = behavior === PRODUCT_BEHAVIOR.WEIGHT;

      try {
        const base = {
          name: p.name,
          formula: p.formula,
          type: p.type,
          manufacturedBy: p.manufacturedBy,
          picture: p.picture,
          discounts: discountLabel,
          discountMode: "auto",
          description: p.description,
          usage: p.usage,
          createdAt: new Date(),
        };

        let extra = {};
        if (isMedicine) {
          const pPrc = p.packEnabled && p.packPrice ? parseFloat(p.packPrice) : 0;
          const sPrc = p.stripEnabled && p.stripPrice ? parseFloat(p.stripPrice) : 0;
          extra = {
            quantityUnit: "medicine",
            packaging: {
              stripsPerPack: p.stripsPerPack ? parseInt(p.stripsPerPack, 10) : 0,
              tabletsPerStrip: p.tabletsPerStrip ? parseInt(p.tabletsPerStrip, 10) : 0,
            },
            sellingOptions: {
              pack: { enabled: p.packEnabled, label: "Full Pack", price: pPrc, stock: p.packStock ? parseInt(p.packStock, 10) : 0 },
              strip: { enabled: p.stripEnabled, label: "Strip", price: sPrc, stock: p.stripStock ? parseInt(p.stripStock, 10) : 0 },
            },
            price: p.packEnabled && pPrc ? pPrc : sPrc,
            retailPrice: p.packEnabled && pPrc ? pPrc : sPrc,
          };
        } else if (isWeight) {
          extra = {
            price: parseFloat(p.price) || 0,
            retailPrice: parseFloat(p.retailPrice) || 0,
            quantityPerPack: p.quantityPerPack || "",
            quantityUnit: "weight",
          };
        } else {
          extra = {
            price: parseFloat(p.price) || 0,
            retailPrice: parseFloat(p.retailPrice) || 0,
            quantityPerPack: p.quantityPerPack ? parseInt(p.quantityPerPack, 10) : 0,
            quantityUnit: "count",
          };
        }

        await addDoc(collection(db, "products"), { ...base, ...extra });
        successCount++;
        setSubmittedCount((c) => c + 1);
      } catch (err) {
        console.error("Failed to add:", p.name, err);
        failCount++;
      }
    });

    await Promise.all(promises);
    setLoading(false);
    isSubmitting.current = false;

    if (failCount === 0) {
      toast.success(`${successCount} product${successCount !== 1 ? "s" : ""} added successfully!`);
      setProducts([emptyProduct()]); // reset so a stale re-trigger cannot re-submit
      navigate(-1);
    } else {
      toast.error(`${successCount} added, ${failCount} failed. Check console for details.`);
    }
  };

  return (
    <div className="upload-page">
      {/* ── Header ── */}
      <div className="upload-container" style={{ marginBottom: "24px" }}>
        <div className="bulk-add-header" style={{ marginBottom: 0 }}>
          <div className="bulk-add-title-row">
            <button
              className="bulk-back-btn"
              onClick={() => navigate(-1)}
              title="Back to Stock Management"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </button>
            <div>
              <h2>Bulk Add Products</h2>
              <p className="admin-page-subtitle" style={{ marginBottom: 0 }}>
                Add multiple products at once. All will be synced to Firebase in real-time.
              </p>
            </div>
          </div>
          <div className="bulk-add-actions">
            <span className="bulk-count-badge">{products.length} product{products.length !== 1 ? "s" : ""}</span>
            <button className="bulk-add-row-btn" onClick={addNewRow} disabled={loading}>
              <i className="fa-solid fa-plus"></i> Add Another Product
            </button>
          </div>
        </div>
      </div>

      {/* ── Product Forms ── */}
      <div className="bulk-forms-list">
        {products.map((product, index) => (
          <div key={index} className={`bulk-product-card ${!isRowValid(product) && product.name !== "" ? "bulk-card-invalid" : ""}`}>
            <div className="bulk-card-header">
              <span className="bulk-card-number">Product #{index + 1}</span>
              {isRowValid(product) && (
                <span className="bulk-card-valid-badge">
                  <i className="fa-solid fa-circle-check"></i> Ready
                </span>
              )}
              {products.length > 1 && (
                <button
                  className="bulk-remove-btn"
                  onClick={() => removeRow(index)}
                  disabled={loading}
                  title="Remove this product"
                >
                  <i className="fa-solid fa-trash"></i> Remove
                </button>
              )}
            </div>

            <div className="bulk-form-grid">
              {/* Name */}
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={product.name}
                  onChange={(e) => updateField(index, "name", e.target.value)}
                  list={`bulk-nameOptions-${index}`}
                  placeholder="Enter product name"
                />
                <datalist id={`bulk-nameOptions-${index}`}>
                  {nameOptions.map((n, i) => <option key={i} value={n} />)}
                </datalist>
              </div>

              {/* Formula */}
              <div className="form-group">
                <label>Formula</label>
                <input
                  type="text"
                  value={product.formula}
                  onChange={(e) => updateField(index, "formula", e.target.value)}
                  placeholder="e.g. Paracetamol 500mg"
                />
              </div>

              {/* Category */}
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={product.type}
                  onChange={(e) => updateField(index, "type", e.target.value)}
                >
                  <option value="" disabled>Select a type</option>
                  {categories.map((t, i) => <option key={i} value={t}>{t}</option>)}
                </select>
              </div>

              {(() => {
                const isMed = getProductBehaviorType(product.type) === PRODUCT_BEHAVIOR.MEDICINE;
                const isWgt = getProductBehaviorType(product.type) === PRODUCT_BEHAVIOR.WEIGHT;
                return isMed ? (
                  <div className="bulk-full-width" style={{ marginTop: "10px", padding: "12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef" }}>
                    <div style={{ marginBottom: "12px", fontWeight: "bold", color: "#333" }}><i className="fa-solid fa-pills" /> Medicine Pricing & Packaging</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Strips per Pack</label>
                        <input type="number" min="0" value={product.stripsPerPack} onChange={(e) => updateField(index, "stripsPerPack", e.target.value)} placeholder="e.g. 10" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>Tablets per Strip</label>
                        <input type="number" min="0" value={product.tabletsPerStrip} onChange={(e) => updateField(index, "tabletsPerStrip", e.target.value)} placeholder="e.g. 10" />
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div className="selling-option-card" style={{ border: `2px solid ${product.packEnabled ? "#d2222d" : "#e0e0e0"}`, borderRadius: 8, padding: "12px 14px", background: product.packEnabled ? "#fff8f8" : "#fafafa" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "pointer", marginBottom: product.packEnabled ? 10 : 0 }}>
                          <input type="checkbox" checked={product.packEnabled} onChange={(e) => updateField(index, "packEnabled", e.target.checked)} />
                          Sell as Full Pack
                        </label>
                        {product.packEnabled && (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Pack Price *</label>
                              <input type="number" min="0" value={product.packPrice} onChange={(e) => updateField(index, "packPrice", e.target.value)} placeholder="e.g. 1200" />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Pack Stock</label>
                              <input type="number" min="0" value={product.packStock} onChange={(e) => updateField(index, "packStock", e.target.value)} placeholder="e.g. 20" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="selling-option-card" style={{ border: `2px solid ${product.stripEnabled ? "#d2222d" : "#e0e0e0"}`, borderRadius: 8, padding: "12px 14px", background: product.stripEnabled ? "#fff8f8" : "#fafafa" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "pointer", marginBottom: product.stripEnabled ? 10 : 0 }}>
                          <input type="checkbox" checked={product.stripEnabled} onChange={(e) => updateField(index, "stripEnabled", e.target.checked)} />
                          Sell as Strip
                        </label>
                        {product.stripEnabled && (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Strip Price *</label>
                              <input type="number" min="0" value={product.stripPrice} onChange={(e) => updateField(index, "stripPrice", e.target.value)} placeholder="e.g. 140" />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label>Strip Stock</label>
                              <input type="number" min="0" value={product.stripStock} onChange={(e) => updateField(index, "stripStock", e.target.value)} placeholder="e.g. 80" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Price */}
                    <div className="form-group">
                      <label>Price (Selling Price) *</label>
                      <input type="number" value={product.price} onChange={(e) => updateField(index, "price", e.target.value)} placeholder="Enter selling price" min="0" />
                    </div>

                    {/* Retail Price */}
                    <div className="form-group">
                      <label>Retail Price (Original Price)</label>
                      <input type="number" value={product.retailPrice} onChange={(e) => updateField(index, "retailPrice", e.target.value)} placeholder="Enter original/retail price" min="0" />
                    </div>

                    {/* Size Per Pack */}
                    <div className="form-group">
                      <label>
                        {isWgt ? "Pack Size / Weight" : "Size"}
                        {isWgt && (
                          <span style={{ marginLeft: "8px", fontSize: "11px", background: "#1565c0", color: "#fff", borderRadius: "4px", padding: "2px 7px", fontWeight: "600" }}>
                            Weight / Volume
                          </span>
                        )}
                      </label>
                      <input type="text" value={product.quantityPerPack} onChange={(e) => updateField(index, "quantityPerPack", e.target.value)} list={`bulk-qtyOptions-${index}`} placeholder={isWgt ? "e.g. 200ml, 500ml, 1kg, 2 Liter" : "e.g. 10, 20, 100"} min={isWgt ? undefined : "0"} />
                      <datalist id={`bulk-qtyOptions-${index}`}>
                        {(WEIGHT_QUANTITY_SUGGESTIONS[product.type] || []).map((s, i) => <option key={i} value={s} />)}
                      </datalist>
                      {isWgt && (
                        <small style={{ color: "#1565c0", marginTop: "4px", display: "block" }}>
                          Enter the pack size or weight (e.g. <strong>1kg</strong>, <strong>500ml</strong>, <strong>2 Liter</strong>).
                        </small>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Manufactured By */}
              <div className="form-group">
                <label>Manufactured By</label>
                <input
                  type="text"
                  value={product.manufacturedBy}
                  onChange={(e) => updateField(index, "manufacturedBy", e.target.value)}
                  list={`bulk-brandOptions-${index}`}
                  placeholder="Manufacturer / brand name"
                />
                <datalist id={`bulk-brandOptions-${index}`}>
                  {manufacturedByOptions.map((b, i) => <option key={i} value={b} />)}
                </datalist>
              </div>

              {/* Image Upload — full width */}
              <div className="bulk-full-width">
                <CloudinaryUpload
                  id={`bulk-picture-${index}`}
                  label="Product Image"
                  required
                  value={product.picture}
                  onChange={(url) => updateField(index, "picture", url)}
                />
              </div>

              {/* Description — full width */}
              <div className="form-group bulk-full-width">
                <label>Description</label>
                <input
                  type="text"
                  value={product.description}
                  onChange={(e) => updateField(index, "description", e.target.value)}
                  list={`bulk-descOptions-${index}`}
                  placeholder="Brief description about the medicine"
                />
                <datalist id={`bulk-descOptions-${index}`}>
                  {descriptionOptions.map((d, i) => <option key={i} value={d} />)}
                </datalist>
              </div>

              {/* Usage — full width */}
              <div className="form-group bulk-full-width">
                <label>Usage Instructions</label>
                <input
                  type="text"
                  value={product.usage}
                  onChange={(e) => updateField(index, "usage", e.target.value)}
                  list={`bulk-usageOptions-${index}`}
                  placeholder="How to use this medicine"
                />
                <datalist id={`bulk-usageOptions-${index}`}>
                  {usageOptions.map((u, i) => <option key={i} value={u} />)}
                </datalist>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Bottom action bar ── */}
      <div className="bulk-bottom-bar">
        <button className="bulk-add-row-btn" onClick={addNewRow} disabled={loading}>
          <i className="fa-solid fa-plus"></i> Add Another Product
        </button>
        <button
          className="bulk-submit-btn"
          onClick={handleSubmitAll}
          disabled={loading || !allValid}
        >
          {loading
            ? <><i className="fa-solid fa-spinner fa-spin"></i> Submitting {submittedCount}/{products.length}...</>
            : <><i className="fa-solid fa-cloud-arrow-up"></i> Submit All ({products.length})</>
          }
        </button>
      </div>
    </div>
  );
}

export default BulkAddProduct;
